import { useEffect, useMemo, useState } from 'react';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { reconstructOrderTraderSide } from '@/lib/kalshiApi/orderReadV2';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';
import { onActiveChange, subscribePush, topicActive } from '@/services/relayPush';

// Per-ticker view of the logged-in user's open Kalshi exposure.
// Positions are summed per side so the POS chip can render a proportional
// YES/NO arc when a user holds both. Orders carry their Kalshi-native
// queue_position; cumulative "level + better" depth is computed at render
// time against the live orderbook (so it ticks with hydra without a refetch).
//
// SHARED POLLER (Jun 27): this hook is mounted at ~6 sites in a live window
// (Value Dashboard, three GameDetail sections, Spreads/Totals, Position
// Tracker), and each mount used to run its OWN /api/kalshi/me/snapshot poll
// every 5s — so one window fired the same request 4-6x over, multiplying the
// browser→relay HTTP/1.1 connection-pool pressure that makes extra windows
// "exponentially slower". Now a SINGLE module-level poller unions every
// subscriber's ticker needs into ONE /me/snapshot request and each hook
// filters the shared snapshot down to its OWN tickers — byte-identical output
// per consumer (no table ever sees another table's tickers), one request
// instead of N. Same within-window dedup pattern as useHeadroom.

export interface UserPositionSlice {
  yesCount: number;
  noCount: number;
}

export interface UserOrderSlice {
  orderId: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  priceCents: number;
  remainingCount: number;
  queuePosition: number | null;
}

export interface UserKalshiPOS {
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface RawPosition {
  ticker?: unknown;
  side?: unknown;
  netCount?: unknown;
}

interface RawOrder {
  order_id?: unknown;
  ticker?: unknown;
  outcome_side?: unknown;
  side?: unknown;
  action?: unknown;
  yes_price_dollars?: unknown;
  no_price_dollars?: unknown;
  remaining_count_fp?: unknown;
  yes_price?: unknown;
  no_price?: unknown;
  remaining_count?: unknown;
  queue_position_fp?: unknown;
  queue_position?: unknown;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asFloat(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function extractList<T>(payload: unknown, key: string): T[] {
  if (!payload || typeof payload !== 'object') return [];
  const list = (payload as Record<string, unknown>)[key];
  return Array.isArray(list) ? (list as T[]) : [];
}

const POLL_MS = 5000;

// ── module-level shared snapshot + poller ───────────────────────────────────
// One poll loop per browser window, shared across every mounted hook. The maps
// hold the UNION of all subscribers' tickers; each hook filters to its own.
let sharedPos = new Map<string, UserPositionSlice>();
let sharedOrders = new Map<string, UserOrderSlice[]>();
let sharedError: string | null = null;
let sharedLoading = false;

interface Sub {
  tickers: ReadonlySet<string>;
  notify: () => void;
}
const subs = new Set<Sub>();
let stopPollTimer: (() => void) | null = null;
let inFlight = false;
let pendingRefetch = false;
// Bumped on logout/reset. A fetch captures the epoch before its await and
// discards its result if the epoch moved — so an in-flight poll that started as
// User A can never write A's positions into the cache AFTER a logout wiped it.
let epoch = 0;

function notifyAll(): void {
  for (const s of subs) s.notify();
}

/** Union of all NON-EMPTY subscriber ticker sets, sorted. Empty subscribers
 *  (a table with no visible Kalshi tickers / no open orders) contribute nothing
 *  and render nothing — we deliberately do NOT widen to a no-filter "fetch all"
 *  poll, because the relay only enriches queue_position when market_tickers is
 *  supplied, so an all-poll would strip queue data from every other consumer. */
function unionTickers(): string[] {
  const u = new Set<string>();
  for (const s of subs) for (const t of s.tickers) u.add(t);
  return Array.from(u).sort();
}

function parseSnapshot(json: unknown): {
  pos: Map<string, UserPositionSlice>;
  orders: Map<string, UserOrderSlice[]>;
} {
  const rawPositions = extractList<RawPosition>(json, 'positions');
  const rawOrders = extractList<RawOrder>(json, 'orders');

  const posMap = new Map<string, UserPositionSlice>();
  for (const p of rawPositions) {
    const ticker = asString(p.ticker);
    const side = asString(p.side);
    const net = asNumber(p.netCount);
    if (!ticker || !net || net <= 0) continue;
    if (side !== 'yes' && side !== 'no') continue;
    const slice = posMap.get(ticker) ?? { yesCount: 0, noCount: 0 };
    if (side === 'yes') slice.yesCount += net;
    else slice.noCount += net;
    posMap.set(ticker, slice);
  }

  const ordMap = new Map<string, UserOrderSlice[]>();
  for (const o of rawOrders) {
    const orderId = asString(o.order_id);
    const ticker = asString(o.ticker);
    // Reconstruct the trader-native {side, action} for DISPLAY. V2 is single-
    // book: a resting buy-NO reads back side:'yes'/action:'sell'/outcome_side:'no',
    // so reading legacy `side` first mislabeled the overlay's NO orders as YES
    // on the wrong ladder leg. reconstructOrderTraderSide derives side from
    // outcome_side and flips action when cross-framed — the same adapter
    // parseOrders uses. Orders only; fills keep the legacy pair.
    const { side: sideRaw, action: actionRaw } = reconstructOrderTraderSide(o);
    const remaining =
      asFloat(o.remaining_count_fp) ?? asNumber(o.remaining_count) ?? 0;
    if (!orderId || !ticker || remaining <= 0) continue;
    const yesDollars = asFloat(o.yes_price_dollars);
    const noDollars = asFloat(o.no_price_dollars);
    let yesPx = yesDollars != null ? Math.round(yesDollars * 100) : asNumber(o.yes_price);
    let noPx = noDollars != null ? Math.round(noDollars * 100) : asNumber(o.no_price);
    // Kalshi frequently sends only ONE leg's price — a NO order commonly
    // arrives as `yes_price` only (CLAUDE.md venue quirk: "derive 100 − yes_price
    // when the side-matching field is missing or zero"). Derive the missing/zero
    // side from the complement so a resting NO order isn't silently dropped from
    // the POS overlay. Mirrors PositionSidebar / accountStore / TraderDetail.
    if ((yesPx == null || yesPx === 0) && noPx != null && noPx !== 0) yesPx = 100 - noPx;
    if ((noPx == null || noPx === 0) && yesPx != null && yesPx !== 0) noPx = 100 - yesPx;
    const priceCents = sideRaw === 'yes' ? yesPx : noPx;
    if (priceCents == null) continue;
    const queuePosition = asFloat(o.queue_position_fp) ?? asNumber(o.queue_position);
    const slice: UserOrderSlice = {
      orderId,
      side: sideRaw,
      action: actionRaw,
      priceCents,
      remainingCount: remaining,
      queuePosition,
    };
    const list = ordMap.get(ticker) ?? [];
    list.push(slice);
    ordMap.set(ticker, list);
  }

  return { pos: posMap, orders: ordMap };
}

async function fetchOnce(): Promise<void> {
  // Single-flight: never stack concurrent snapshots. If asked again mid-flight,
  // remember to re-poll once the current one settles (covers a just-added
  // ticker set without waiting a full interval).
  if (inFlight) {
    pendingRefetch = true;
    return;
  }
  const union = unionTickers();
  if (union.length === 0) {
    // No subscriber needs any ticker — present empty, skip the network call.
    // Only notify when there was data to clear (avoids an idle every-5s churn).
    if (sharedPos.size || sharedOrders.size) {
      sharedPos = new Map();
      sharedOrders = new Map();
      sharedError = null;
      notifyAll();
    }
    return;
  }
  inFlight = true;
  sharedLoading = true;
  const myEpoch = epoch;
  try {
    const url = `/api/kalshi/me/snapshot?market_tickers=${encodeURIComponent(union.join(','))}`;
    const res = await apiFetch(url);
    if (myEpoch !== epoch) return; // a logout/reset raced us — discard this result.
    if (!res.ok) {
      // apiFetch does NOT throw on non-2xx. A 401/429/5xx must KEEP the last
      // snapshot, not blank it — a confidently-empty positions/orders overlay on
      // a money surface is the dangerous failure. Surface the error instead.
      sharedError = `snapshot failed (${res.status})`;
      return;
    }
    const json: unknown = await res.json();
    if (myEpoch !== epoch) return;
    const parsed = parseSnapshot(json);
    sharedPos = parsed.pos;
    sharedOrders = parsed.orders;
    sharedError = null;
  } catch (err) {
    if (myEpoch !== epoch) return;
    // Keep the last snapshot on a thrown (network) error too — don't blank.
    sharedError = err instanceof Error ? err.message : 'Failed to load activity';
  } finally {
    // Only release the single-flight latch + drive UI if we're STILL the current
    // epoch. A stale-epoch fetch (a logout/reset moved epoch while we were in
    // flight) must NOT clear a live re-login fetch's inFlight — reset already
    // cleared it — else single-flight breaks for one tick across that race.
    if (myEpoch === epoch) {
      inFlight = false;
      sharedLoading = false;
      notifyAll();
      if (pendingRefetch) {
        pendingRefetch = false;
        void fetchOnce();
      }
    }
  }
}

function ensurePolling(): void {
  if (stopPollTimer) return;
  // Push-fed when the relay stream serves the pos topic (org-scope users);
  // the poll timer only arms as fallback (incl. personal-key scope, which
  // the server marks unavailable).
  if (topicActive('pos')) return;
  // Hidden tabs poll the me/snapshot slice at 1/6 rate; immediate on refocus.
  stopPollTimer = startVisibilityAwareInterval(() => void fetchOnce(), POLL_MS);
}
function stopPolling(): void {
  if (stopPollTimer) {
    stopPollTimer();
    stopPollTimer = null;
  }
}

// Stream-fed slice: frames are the /api/kalshi/me/snapshot shape (queue
// merged into orders) for the user's FULL slice — parseSnapshot applies
// unchanged and subscribers keep filtering to their own tickers locally.
subscribePush('pos', (payload) => {
  const parsed = parseSnapshot(payload);
  sharedPos = parsed.pos;
  sharedOrders = parsed.orders;
  sharedError = null;
  notifyAll();
});
onActiveChange(() => {
  if (subs.size === 0) return;
  if (topicActive('pos')) stopPolling();
  else ensurePolling();
});

/** Wipe the shared snapshot on logout so a different user never reads stale
 *  positions/orders. Mirrors resetHeadroomCache — wired into authStore logout. */
export function resetUserKalshiPOSCache(): void {
  epoch += 1; // invalidate any in-flight fetch so it can't write post-reset.
  sharedPos = new Map();
  sharedOrders = new Map();
  sharedError = null;
  inFlight = false;
  sharedLoading = false;
  pendingRefetch = false;
  stopPolling();
  // Do NOT clear `subs` — the auth-gated hook effects own their subscribe/
  // unsubscribe lifecycle; clearing here would desync subs.size from the live
  // mounts. On re-auth they re-subscribe and ensurePolling() restarts the loop.
}

function filterToOwn<V>(m: Map<string, V>, own: ReadonlySet<string>): Map<string, V> {
  const out = new Map<string, V>();
  for (const t of own) {
    const v = m.get(t);
    if (v !== undefined) out.set(t, v);
  }
  return out;
}

export function useUserKalshiPOS(tickers: string[]): UserKalshiPOS {
  // Gate on auth like the sibling shared pollers (useHeadroom / useRateLimitStatus)
  // so the loop never fires /me/snapshot while logged out (no stale-token polls)
  // and so a logout tears the subscription down.
  const isAuthed = useAuthStore((s) => s.status === 'authenticated');
  const tickerKey = useMemo(() => tickers.slice().sort().join('|'), [tickers]);
  // Re-render trigger: the shared poller bumps this on every settled snapshot.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isAuthed) return;
    const set: ReadonlySet<string> = new Set(tickerKey ? tickerKey.split('|') : []);
    const sub: Sub = { tickers: set, notify: () => setTick((t) => t + 1) };
    subs.add(sub);
    ensurePolling();
    // Cover this subscriber's tickers promptly — a freshly-opened game/table
    // shouldn't wait up to 5s for its positions/orders.
    void fetchOnce();
    // Surface whatever's already cached without waiting for the next poll.
    setTick((t) => t + 1);
    return () => {
      subs.delete(sub);
      if (subs.size === 0) stopPolling();
    };
  }, [tickerKey, isAuthed]);

  const ownSet = useMemo(
    () => new Set(tickerKey ? tickerKey.split('|') : []),
    [tickerKey],
  );

  // Filter the shared snapshot down to THIS consumer's tickers — byte-identical
  // to the old per-mount hook (no consumer ever sees another table's tickers).
  // `tick` in the deps recomputes the view when a fresh snapshot lands; sharedPos
  // /sharedOrders are module-level (non-reactive) so they're intentionally not deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const positionByTicker = useMemo(() => filterToOwn(sharedPos, ownSet), [ownSet, tick]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ordersByTicker = useMemo(() => filterToOwn(sharedOrders, ownSet), [ownSet, tick]);

  return {
    positionByTicker,
    ordersByTicker,
    loading: sharedLoading,
    error: sharedError,
    refresh: fetchOnce,
  };
}
